import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillTransfer } from '@fortawesome/free-solid-svg-icons';
import { SectionTitle, EndpointDoc, InfoCard } from '../../../components/apidocs';
import { PAYOUT_ENDPOINTS } from '../data/payout';

const PayoutSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div>
      <SectionTitle icon={faMoneyBillTransfer} iconColor="bg-gradient-to-br from-emerald-400 to-green-500">
        Payout API
      </SectionTitle>
      <p className="text-gray-500 dark:text-slate-400 mb-5 text-sm">{t('apiDocs.payoutApiDesc')}</p>

      <InfoCard type="info" title="Payout Wallet">
        {t('apiDocs.payoutWalletNote')}
      </InfoCard>

      {PAYOUT_ENDPOINTS(t).map((spec, idx) => <EndpointDoc key={idx} {...spec} />)}

      {/* ═══ โหมดจ่ายแบบ P2P (เงินเข้าเป็นงวด) — สำหรับร้านที่เปิดใช้งาน ═══ */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200/60 dark:border-indigo-800 rounded-xl p-5 mb-6">
        <p className="text-indigo-700 dark:text-indigo-300 font-bold text-sm mb-2 flex items-center gap-2">
          <FontAwesomeIcon icon={faMoneyBillTransfer} /> โหมดจ่ายแบบ P2P — เงินอาจเข้าบัญชีเป็นหลายยอด (สำหรับร้านที่เปิดใช้งาน)
        </p>
        <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-3">
          ถ้าร้านของคุณเปิดโหมด P2P ระบบอาจจ่ายยอดถอน 1 รายการเป็น<strong>หลายงวด จากผู้โอนหลายบัญชี</strong> แทนการโอนก้อนเดียว
          — วิธีเรียก API และ response <strong>เหมือนเดิมทุกอย่าง</strong> ต่างแค่พฤติกรรมตอนรับเงิน:
        </p>
        <ul className="space-y-1.5 text-sm text-indigo-700 dark:text-indigo-300 list-disc list-inside mb-3">
          <li>เงินเข้าบัญชีปลายทางเดิมที่ระบุใน payout เสมอ แต่ทยอยเข้า<strong>ทีละงวด</strong> (คนละเวลา คนละชื่อผู้โอน) รวมกันครบเต็ม <code className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">amount</code> แน่นอน</li>
          <li>สถานะจะเป็น <code className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">pending</code> ไปจนกว่ายอดครบ แล้วเปลี่ยนเป็น <code className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">completed</code> ทันที (ไม่ผ่านขั้น approved)</li>
          <li>ทุกงวดที่เงินเข้า ระบบยิง webhook <code className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">payout.funded</code> บอกยอดงวดนี้/ยอดสะสม/ยอดคงเหลือ และยิง <code className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">payout.completed</code> เมื่อครบยอด (ดูตัวอย่างในแท็บ Webhooks)</li>
          <li><code className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">net_amount</code> = ยอดเต็มเสมอ — ค่าธรรมเนียมถอนหักจากเครดิตค่าธรรมเนียมที่เติมไว้ล่วงหน้า ไม่ได้หักจากยอดเงินที่โอนเข้าบัญชี</li>
          <li><code className="font-mono text-xs bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">slip_url</code> อาจเป็น <code className="font-mono text-xs">null</code> (การโอนมาจากหลายผู้โอน ไม่มีสลิปใบเดียว)</li>
        </ul>
        <div className="rounded-lg bg-white/60 dark:bg-slate-800/40 border border-indigo-200/60 dark:border-indigo-800 p-3 text-xs text-indigo-700 dark:text-indigo-300">
          <strong>ข้อควรระวัง:</strong> อย่าปิดรายการฝั่งระบบคุณเมื่อเห็น "เงินเข้าครั้งแรก" — ให้ยึด webhook{' '}
          <code className="font-mono bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">payout.completed</code> หรือเช็ค{' '}
          <code className="font-mono bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">GET /payout/:payout_number</code> จน{' '}
          <code className="font-mono bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">status = "completed"</code> เท่านั้น
          และตอนกระทบยอดบัญชีธนาคาร ให้รวมยอดโอนย่อยทุกงวดเทียบกับ <code className="font-mono bg-indigo-100 dark:bg-indigo-900/40 px-1 rounded">funded_amount</code> ล่าสุด ไม่ใช่มองหายอดโอนก้อนเดียว
        </div>
      </div>
    </div>
  );
};

export default PayoutSection;
